
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import React from 'react';
import { GhostOSArchitectureDiagram } from '../../Diagrams';
import { Shield, Layers, Cpu, Network, Zap, Lock, Database, GitBranch, Share2 } from 'lucide-react';

const LayerItem = ({ title, items, icon: Icon }: { title: string; items: string[]; icon: any }) => (
    <div className="p-6 bg-white dark:bg-white/[0.02] border border-stone-200 dark:border-white/10 rounded-2xl group hover:border-nobel-gold/30 transition-all">
        <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-stone-100 dark:bg-white/5 rounded-lg text-stone-900 dark:text-white group-hover:bg-nobel-gold/10 group-hover:text-nobel-gold transition-colors">
                <Icon size={18} />
            </div>
            <h4 className="font-bold text-stone-900 dark:text-white uppercase tracking-widest text-[10px]">{title}</h4>
        </div>
        <ul className="space-y-2">
            {items.map((item, i) => (
                <li key={i} className="text-xs text-stone-500 dark:text-stone-400 flex items-center gap-2">
                    <div className="w-1 h-1 rounded-full bg-stone-300 dark:bg-white/20" />
                    {item}
                </li>
            ))}
        </ul>
    </div>
);

export const Architecture = () => (
  <div className="space-y-16 animate-fade-in">
    <section className="max-w-3xl">
        <h3 className="text-3xl font-serif font-bold text-stone-900 dark:text-white mb-6 tracking-tight">System Infrastructure & Design</h3>
        <p className="text-lg text-stone-600 dark:text-stone-300 leading-relaxed font-light">
            GhostPay is architected as a <span className="text-stone-900 dark:text-white font-bold">multi-layered, privacy-preserving infrastructure</span>. We utilize a unique modular approach that separates user interaction from financial execution, utilizing Neon for high-throughput ledgers and Supabase for realtime dashboards.
        </p>
    </section>

    {/* Layered Stack Grid */}
    <section>
        <div className="grid md:grid-cols-3 gap-6">
            <LayerItem 
                title="L1: Interaction Layer"
                icon={Network}
                items={["Terminal POS Interfaces", "GhostWallet Mobile SDK", "x402 Agent Gateway", "Supabase Realtime (WebSockets)"]}
            />
            <LayerItem 
                title="L2: Secure Execution"
                icon={Cpu}
                items={["GhostOS Isolated Runtime", "Neon Serverless Postgres (Ledger)", "Parallel Event Indexing", "Fraud Detection Engine"]}
            />
            <LayerItem 
                title="L3: Settlement Layer"
                icon={Database}
                items={["Solana Mainnet (Anchor)", "Sui Network (Move)", "GHOST Treasury Vaults", "Multi-Sig DAO Custody"]}
            />
        </div>
    </section>

    {/* Deep Technical Deep Dive */}
    <section className="grid lg:grid-cols-2 gap-12 items-start">
        <div className="space-y-6">
            <h3 className="text-2xl font-serif font-bold text-stone-900 dark:text-white">Data Isolation Strategy</h3>
            <p className="text-sm text-stone-500 dark:text-stone-400 leading-relaxed">
                GhostPay utilizes a dual-database architecture to ensure maximum performance and security:
            </p>
            <div className="grid gap-4">
                <div className="flex gap-4 p-4 rounded-xl bg-stone-50 dark:bg-white/[0.02] border border-stone-100 dark:border-white/10">
                    <Database className="text-nobel-gold shrink-0" size={20} />
                    <div>
                        <h5 className="font-bold text-sm text-stone-900 dark:text-white">Neon (The Ledger)</h5>
                        <p className="text-[11px] text-stone-500 leading-relaxed">High-throughput, serverless Postgres. Handles millions of transaction logs, escrow states, and AI agent activity logs. Strictly write-heavy.</p>
                    </div>
                </div>
                <div className="flex gap-4 p-4 rounded-xl bg-stone-50 dark:bg-white/[0.02] border border-stone-100 dark:border-white/10">
                    <Share2 className="text-nobel-gold shrink-0" size={20} />
                    <div>
                        <h5 className="font-bold text-sm text-stone-900 dark:text-white">Supabase (The Dashboard)</h5>
                        <p className="text-[11px] text-stone-500 leading-relaxed">Handles Authentication, user profiles, and Realtime WebSockets for the frontend dashboard. Synced asynchronously from Neon.</p>
                    </div>
                </div>
            </div>
        </div>
        <div className="bg-stone-900 rounded-[32px] p-8 border border-white/10 shadow-2xl relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-8 opacity-10 blur-xl group-hover:opacity-20 transition-opacity">
                <Shield size={200} className="text-white" />
            </div>
            <div className="relative z-10">
                <h4 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
                    <Lock size={18} className="text-nobel-gold" /> GhostOS Security Guardrails
                </h4>
                <ul className="space-y-4">
                    {[
                        "Hardware-isolated keys for merchant instances.",
                        "End-to-end encrypted metadata transmission.",
                        "Zero-Knowledge proofs for transaction verification.",
                        "Automated 2-of-3 multi-sig for treasury withdrawals."
                    ].map((guard, i) => (
                        <li key={i} className="flex gap-3 text-xs text-gray-400 leading-relaxed">
                            <span className="text-nobel-gold font-bold">{i + 1}.</span>
                            {guard}
                        </li>
                    ))}
                </ul>
                <div className="mt-8 pt-6 border-t border-white/5">
                    <div className="text-[10px] font-bold text-stone-500 uppercase tracking-widest mb-1">Architecture Reference</div>
                    <div className="text-xs font-mono text-gray-500">sys.ghost_os.v1.0.0_beta</div>
                </div>
            </div>
        </div>
    </section>

    {/* Visualization Component */}
    <section>
        <div className="flex items-center justify-between mb-8">
            <h3 className="text-2xl font-serif font-bold text-stone-900 dark:text-white">Execution Flow</h3>
            <span className="text-[10px] font-bold text-stone-400 uppercase tracking-[0.3em]">Technical Diagram</span>
        </div>
        <GhostOSArchitectureDiagram />
    </section>
  </div>
);
